// Webhook endpoints and deliveries (FR-44; company_admin only). The signing
// secret is shown once, on creation and after each rotation, and only in
// component state. Each endpoint's delivery history can be resent.
import {
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { companyKey, ConfirmDialog, EnvironmentBadge, SecretOnce, type Company } from "./common";

type EventType = Schemas["WebhookEventType"];
type Endpoint = Schemas["WebhookEndpoint"];
type Delivery = Schemas["WebhookDelivery"];

const EVENTS: EventType[] = [
  "consent.updated",
  "consent.revoked",
  "one_time.approved",
  "one_time.declined",
  "stk.completed",
  "account.phone_changed",
  "access.approved",
  "access.not_approved",
  "till_payment.completed",
];

const DELIVERY_COLOUR: Record<Delivery["status"], string> = { pending: "yellow", delivered: "green", dead: "red" };

const webhooksKey = (companyId: string) => companyKey(companyId, "webhooks");

function useWebhooks(companyId: string) {
  return useQuery({
    queryKey: webhooksKey(companyId),
    queryFn: () => unwrap(api.GET("/companies/{company_id}/webhooks", { params: { path: { company_id: companyId } } })),
  });
}

/** eventLabel names a webhook event in the current language. */
function useEventLabel() {
  const { t } = useTranslation();
  return (e: string) => t(`webhook_event.${e.replace(".", "_")}`, { defaultValue: e });
}

export function WebhooksTab({ company }: { company: Company }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const webhooks = useWebhooks(company.id);
  const [secret, setSecret] = useState<{ title: string; value: string } | null>(null);
  const [rotating, setRotating] = useState<Endpoint | null>(null);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const items = webhooks.data?.items ?? [];
  const refresh = () => void queryClient.invalidateQueries({ queryKey: webhooksKey(company.id) });

  const rotate = async () => {
    if (!rotating) return;
    setRotateError(null);
    setBusy(true);
    const { data, error } = await api.POST("/companies/{company_id}/webhooks/{endpoint_id}/rotate-secret", {
      params: { path: { company_id: company.id, endpoint_id: rotating.id } },
    });
    setBusy(false);
    if (!data) return setRotateError(errorMessage(error, t("error.generic")));
    setRotating(null);
    setNotice(null);
    setSecret({ title: t("webhooks.rotated_title", { url: data.url }), value: data.secret });
    refresh();
  };

  const update = async (e: Endpoint, body: { url?: string; events?: EventType[]; active?: boolean }) => {
    setError(null);
    setNotice(null);
    const { data, error } = await api.PATCH("/companies/{company_id}/webhooks/{endpoint_id}", {
      params: { path: { company_id: company.id, endpoint_id: e.id } },
      body,
    });
    if (!data) {
      const message = errorMessage(error, t("error.generic"));
      setError(message);
      return message;
    }
    setNotice(t("webhooks.updated", { url: data.url }));
    refresh();
    return null;
  };

  return (
    <Stack gap="md">
      <Text size="sm">{t("webhooks.intro")}</Text>
      {notice && <Notice>{notice}</Notice>}
      <FormError message={error} />
      {secret ? (
        <SecretOnce
          title={secret.title}
          values={[{ key: "webhook_secret", label: t("webhooks.secret"), value: secret.value }]}
          onDone={() => setSecret(null)}
        />
      ) : (
        <CreateEndpoint
          company={company}
          onCreated={(e) => {
            setNotice(null);
            setSecret({ title: t("webhooks.created_title", { url: e.url }), value: e.secret });
            refresh();
          }}
        />
      )}
      <Title order={2} size="h4">
        {t("webhooks.list")}
      </Title>
      {webhooks.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : webhooks.isError ? (
        <FormError message={errorMessage(webhooks.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text size="sm" c="dimmed" data-testid="webhooks-empty">
          {t("webhooks.empty")}
        </Text>
      ) : (
        <Stack gap="sm" component="ul" p={0} m={0} style={{ listStyle: "none" }} aria-label={t("webhooks.list")}>
          {items.map((e) => (
            <li key={e.id}>
              <EndpointCard
                companyId={company.id}
                endpoint={e}
                onSave={(body) => update(e, body)}
                onRotate={() => {
                  setRotateError(null);
                  setRotating(e);
                }}
              />
            </li>
          ))}
        </Stack>
      )}
      <ConfirmDialog
        opened={rotating !== null}
        title={t("webhooks.rotate_title")}
        confirmLabel={t("webhooks.rotate")}
        busy={busy}
        error={rotateError}
        onConfirm={() => void rotate()}
        onCancel={() => setRotating(null)}
      >
        {t("webhooks.rotate_body", { url: rotating?.url ?? "" })}
      </ConfirmDialog>
    </Stack>
  );
}

function EndpointCard({
  companyId,
  endpoint: e,
  onSave,
  onRotate,
}: {
  companyId: string;
  endpoint: Endpoint;
  onSave: (body: { url?: string; events?: EventType[]; active?: boolean }) => Promise<string | null>;
  onRotate: () => void;
}) {
  const { t } = useTranslation();
  const f = useFormat();
  const eventLabel = useEventLabel();
  const [editing, setEditing] = useState(false);
  return (
    <Paper withBorder p="md" radius="md" data-testid="webhook-endpoint">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" gap="xs">
          <Text fw={600} style={{ wordBreak: "break-all" }} data-testid="webhook-url">
            {e.url}
          </Text>
          <Group gap={4}>
            <Badge color={e.active ? "green" : "gray"} variant="light">
              {e.active ? t("webhooks.active") : t("webhooks.paused")}
            </Badge>
            <EnvironmentBadge sandbox={e.sandbox} />
          </Group>
        </Group>
        <Text size="sm">
          {t("webhooks.events")}: {e.events.map(eventLabel).join(", ")}
        </Text>
        <Text size="xs" c="dimmed">
          {t("webhooks.secret_rotated", { date: f.dateTime(e.secret_rotated_at) })}
        </Text>
        {editing ? (
          <EndpointForm
            initial={{ url: e.url, events: e.events }}
            submitLabel={t("webhooks.save")}
            onSubmit={async (d) => {
              const err = await onSave(d);
              if (!err) setEditing(false);
              return err;
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <Group gap="xs">
            <Button
              component={Link}
              to={`/business/${companyId}/webhooks/${e.id}`}
              size="xs"
              variant="light"
              aria-label={t("webhooks.deliveries_for", { url: e.url })}
            >
              {t("webhooks.deliveries")}
            </Button>
            <Button
              size="xs"
              variant="default"
              onClick={() => setEditing(true)}
              aria-label={t("webhooks.edit_for", { url: e.url })}
            >
              {t("webhooks.edit")}
            </Button>
            <Button
              size="xs"
              variant="default"
              onClick={() => void onSave({ active: !e.active })}
              aria-label={e.active ? t("webhooks.pause_for", { url: e.url }) : t("webhooks.resume_for", { url: e.url })}
            >
              {e.active ? t("webhooks.pause") : t("webhooks.resume")}
            </Button>
            <Button
              size="xs"
              variant="subtle"
              color="red"
              onClick={onRotate}
              aria-label={t("webhooks.rotate_for", { url: e.url })}
            >
              {t("webhooks.rotate")}
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}

type EndpointDraft = { url: string; events: EventType[] };

function EndpointForm({
  initial,
  submitLabel,
  withSandbox,
  onSubmit,
  onCancel,
}: {
  initial: EndpointDraft;
  submitLabel: string;
  withSandbox?: { value: boolean; onChange: (v: boolean) => void };
  onSubmit: (d: EndpointDraft) => Promise<string | null>;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const eventLabel = useEventLabel();
  const [draft, setDraft] = useState<EndpointDraft>(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (draft.events.length === 0) return setError(t("webhooks.pick_event"));
    setError(null);
    setBusy(true);
    try {
      setError(await onSubmit({ url: draft.url.trim(), events: draft.events }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="webhook-form">
      <Stack gap="sm">
        <TextInput
          type="url"
          label={t("webhooks.url")}
          description={t("webhooks.url_hint")}
          value={draft.url}
          onChange={(e) => setDraft({ ...draft, url: e.currentTarget.value })}
          required
        />
        <Checkbox.Group
          label={t("webhooks.events")}
          value={draft.events}
          onChange={(v) => setDraft({ ...draft, events: EVENTS.filter((x) => v.includes(x)) })}
        >
          <Stack gap={6} mt={6}>
            {EVENTS.map((x) => (
              <Checkbox key={x} value={x} label={eventLabel(x)} />
            ))}
          </Stack>
        </Checkbox.Group>
        {withSandbox && (
          <Checkbox
            label={t("webhooks.sandbox")}
            description={t("webhooks.sandbox_hint")}
            checked={withSandbox.value}
            onChange={(e) => withSandbox.onChange(e.currentTarget.checked)}
          />
        )}
        <FormError message={error} />
        <Group gap="xs">
          <Button type="submit" loading={busy}>
            {submitLabel}
          </Button>
          {onCancel && (
            <Button variant="default" onClick={onCancel}>
              {t("common.cancel")}
            </Button>
          )}
        </Group>
      </Stack>
    </form>
  );
}

function CreateEndpoint({
  company,
  onCreated,
}: {
  company: Company;
  onCreated: (e: Schemas["WebhookEndpointWithSecret"]) => void;
}) {
  const { t } = useTranslation();
  const [sandbox, setSandbox] = useState(company.status !== "active");
  const [formKey, setFormKey] = useState(0);
  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Title order={2} size="h4">
          {t("webhooks.create")}
        </Title>
        <EndpointForm
          key={formKey}
          initial={{ url: "", events: ["consent.updated", "consent.revoked"] }}
          submitLabel={t("webhooks.create_submit")}
          withSandbox={{ value: sandbox, onChange: setSandbox }}
          onSubmit={async (d) => {
            const { data, error } = await api.POST("/companies/{company_id}/webhooks", {
              params: { path: { company_id: company.id } },
              body: { ...d, sandbox },
            });
            if (!data) return errorMessage(error, t("error.generic"));
            setFormKey((k) => k + 1);
            onCreated(data);
            return null;
          }}
        />
      </Stack>
    </Paper>
  );
}

/** DeliveriesView lists one endpoint's deliveries, newest first, with resend. */
export function DeliveriesView({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const f = useFormat();
  const eventLabel = useEventLabel();
  const queryClient = useQueryClient();
  const { endpointId = "" } = useParams();
  const webhooks = useWebhooks(companyId);
  const endpoint = webhooks.data?.items.find((e) => e.id === endpointId);
  const key = companyKey(companyId, "deliveries", endpointId);
  const deliveries = useQuery({
    queryKey: key,
    queryFn: () =>
      unwrap(
        api.GET("/companies/{company_id}/webhooks/{endpoint_id}/deliveries", {
          params: { path: { company_id: companyId, endpoint_id: endpointId }, query: { limit: 100 } },
        }),
      ),
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const items = deliveries.data?.items ?? [];

  const resend = async (d: Delivery) => {
    setError(null);
    setNotice(null);
    const { data, error } = await api.POST("/companies/{company_id}/deliveries/{delivery_id}/resend", {
      params: { path: { company_id: companyId, delivery_id: d.id } },
    });
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setNotice(t("deliveries.resent", { event: eventLabel(data.event_type) }));
    void queryClient.invalidateQueries({ queryKey: key });
  };

  return (
    <Stack gap="md">
      <Anchor component={Link} to={`/business/${companyId}/webhooks`} size="sm">
        {t("deliveries.back")}
      </Anchor>
      <Title order={2} size="h4">
        {t("deliveries.title")}
      </Title>
      {endpoint && (
        <Text size="sm" style={{ wordBreak: "break-all" }} data-testid="deliveries-endpoint">
          {endpoint.url}
        </Text>
      )}
      {notice && <Notice>{notice}</Notice>}
      <FormError message={error} />
      {deliveries.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : deliveries.isError ? (
        <FormError message={errorMessage(deliveries.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text size="sm" c="dimmed" data-testid="deliveries-empty">
          {t("deliveries.empty")}
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table verticalSpacing="sm" aria-label={t("deliveries.title")}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("deliveries.event")}</Table.Th>
                <Table.Th>{t("deliveries.status")}</Table.Th>
                <Table.Th>{t("deliveries.attempts")}</Table.Th>
                <Table.Th>{t("deliveries.last_status")}</Table.Th>
                <Table.Th>{t("deliveries.when")}</Table.Th>
                <Table.Th>{t("staff.actions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((d) => (
                <Table.Tr key={d.id} data-testid="delivery-row">
                  <Table.Td>
                    <Text size="sm">{eventLabel(d.event_type)}</Text>
                    <Text size="xs" c="dimmed">
                      {f.dateTime(d.created_at)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={DELIVERY_COLOUR[d.status]} variant="light" data-testid="delivery-status">
                      {t(`delivery_status.${d.status}`)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{d.attempts}</Table.Td>
                  <Table.Td>
                    <Text size="sm">{d.last_status_code ?? "—"}</Text>
                    {d.last_error && (
                      <Text size="xs" c="dimmed" style={{ wordBreak: "break-word" }}>
                        {d.last_error}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {d.status === "pending"
                        ? t("deliveries.next_attempt", { date: f.dateTime(d.next_attempt_at) })
                        : d.status === "delivered" && d.delivered_at
                          ? t("deliveries.delivered_at", { date: f.dateTime(d.delivered_at) })
                          : d.dead_at
                            ? t("deliveries.dead_at", { date: f.dateTime(d.dead_at) })
                            : "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {d.status !== "pending" && (
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => void resend(d)}
                        aria-label={t("deliveries.resend_for", {
                          event: eventLabel(d.event_type),
                          date: f.dateTime(d.created_at),
                        })}
                      >
                        {t("deliveries.resend")}
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
